// src/components/News/NewsList.tsx
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  InputAdornment,
  TextField,
  Card,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Article as ArticleIcon,
} from "@mui/icons-material";
import { fetchNews, deleteNews } from "../../services/api";
import PageHeader from "../common/PageHeader";
import EmptyState from "../common/EmptyState";
import { useSnackbar } from "notistack";

interface News {
  id: number;
  title: string;
  shortDescription: string;
  active: boolean;
  createdAt?: string;
  newsCategoryId?: number;
  categoryTitle?: string;
}

const NewsList: React.FC = () => {
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const {
    data: news,
    isLoading,
    error,
    refetch,
  } = useQuery<News[]>({
    queryKey: ["news"],
    queryFn: fetchNews,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNews,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
      enqueueSnackbar("News article deleted successfully", {
        variant: "success",
      });
    },
    onError: () => {
      enqueueSnackbar("Failed to delete news article", { variant: "error" });
    },
  });

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
  };

  const handleDeleteConfirm = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    navigate("/news/new");
  };

  const filteredNews = news
    ? news.filter(
        (item) =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.shortDescription
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
      )
    : [];

  const paginatedNews = filteredNews.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">
          Error loading news. Please try again.
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title="News Articles"
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "News Articles" },
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add News Article
          </Button>
        }
      />

      <Card sx={{ mb: 3, p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <TextField
            placeholder="Search news articles..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: "100%", sm: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              variant="outlined"
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Card>

      {filteredNews.length === 0 ? (
        <EmptyState
          title="No News Articles Found"
          message={
            searchQuery
              ? "No articles match your search criteria. Try a different search term."
              : "No news articles have been created yet. Add your first article to get started."
          }
          icon={<ArticleIcon />}
          action={{
            label: "Add News Article",
            onClick: handleAddNew,
          }}
        />
      ) : (
        <Paper sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Short Description</TableCell>
                  <TableCell width="150">Category</TableCell>
                  <TableCell width="120">Status</TableCell>
                  <TableCell width="200">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedNews.map((item: News) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <ArticleIcon
                          fontSize="small"
                          sx={{
                            color: theme.palette.primary.main,
                            opacity: 0.7,
                          }}
                        />
                        <Typography fontWeight="medium">
                          {item.title}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{item.shortDescription}</TableCell>
                    <TableCell>
                      {item.categoryTitle ? (
                        <Chip
                          label={item.categoryTitle}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Other
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.active ? "Active" : "Inactive"}
                        color={item.active ? "success" : "default"}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          minWidth: 80,
                          textAlign: "center",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Edit Article">
                          <IconButton
                            component={Link}
                            to={`/news/edit/${item.id}`}
                            size="small"
                            color="primary"
                            sx={{ boxShadow: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Article">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(item.id)}
                            sx={{ boxShadow: 1 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredNews.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
          />
        </Paper>
      )}

      <Dialog
        open={deleteId !== null}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this news article? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewsList;
